import { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '@/entities/session';
import { useOwnStaffRoles } from '@/entities/staff';
import { importOrReload } from '@/shared/lib/import-or-reload';
import { NotFoundPage } from '@/widgets/not-found';
import { SignedInFrame } from './require-auth';

// The whole staff area is one lazy chunk. This gate is the only part customers download, and it
// imports the chunk only after the person turned out to be staff (`app/admin-not-shipped.test.ts`).
const AdminApp = lazy(async () => ({
  default: (await importOrReload(() => import('@/pages/admin/admin-app'))).AdminApp,
}));

/**
 * `/admin/*`. Cosmetic, like every route guard: RLS and `has_staff_permission` (which needs aal2) are the
 * boundary. Signed out → sign in and come back. No staff role → the standard not-found page in the customer
 * shell, exactly as for an unknown path. Staff → the admin app, which handles the second factor itself.
 */
export function AdminGate() {
  const { session, loading } = useSession();
  const location = useLocation();
  const roles = useOwnStaffRoles(session?.user.id);
  if (loading) return null;
  if (!session) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  if (roles.isPending) return null;
  if (roles.isError || !roles.data || roles.data.length === 0) {
    return (
      <SignedInFrame>
        <NotFoundPage />
      </SignedInFrame>
    );
  }
  return (
    <Suspense fallback={null}>
      <AdminApp session={session} roles={roles.data} />
    </Suspense>
  );
}
